import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import {
  GOOGLE_MAPS_AUTH_FAILURE_EVENT,
  loadGoogleMaps,
  isGoogleMapsConfigured,
  resetGoogleMapsLoader,
} from '../../Services/googleMaps';
import {
  getCurrentPosition,
  makeGeoLocation,
  parseGoogleAddressComponents,
  toCoordinate,
} from '../../Utils/geo';
import GoogleMap from './GoogleMap';
import { useI18n } from '../../i18n/I18nProvider';

const AddressLocationPicker = ({
  value = '',
  location = null,
  onAddressChange,
  onLocationChange,
  onValidityChange,
  label,
  placeholder,
  required = false,
  id = 'address-location-picker',
  showMap = true,
  country = 'fr',
}) => {
  const { pick } = useI18n();
  const resolvedLabel = label || pick('Adresse', 'Address');
  const resolvedPlaceholder = placeholder || pick('Recherchez une adresse…', 'Search for an address…');
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const selectedAddressRef = useRef('');
  const callbacksRef = useRef({ onAddressChange, onLocationChange });
  callbacksRef.current = { onAddressChange, onLocationChange };

  const [mapsReady, setMapsReady] = useState(false);
  const [mapsFailed, setMapsFailed] = useState(false);
  const [message, setMessage] = useState('');
  const [locating, setLocating] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const hasCoordinates = Boolean(toCoordinate(location));
  const hasTypedAddress = Boolean(String(value || '').trim());

  useEffect(() => {
    if (toCoordinate(location)) {
      selectedAddressRef.current = String(
        value || location?.formattedAddress || location?.formatted_address || '',
      ).trim();
    } else {
      selectedAddressRef.current = '';
    }
  }, [location]);

  useEffect(() => {
    onValidityChange?.(hasCoordinates);
  }, [hasCoordinates, onValidityChange]);

  useEffect(() => {
    const handleAuthFailure = (event) => {
      setMapsReady(false);
      setMapsFailed(true);
      setMessage(event?.detail?.message || pick('Google Maps a refusé le chargement pour ce domaine.', 'Google Maps refused to load for this domain.'));
    };
    window.addEventListener(GOOGLE_MAPS_AUTH_FAILURE_EVENT, handleAuthFailure);
    return () => window.removeEventListener(GOOGLE_MAPS_AUTH_FAILURE_EVENT, handleAuthFailure);
  }, [pick]);

  useEffect(() => {
    let cancelled = false;
    let listener;

    if (!isGoogleMapsConfigured || !inputRef.current) return undefined;

    loadGoogleMaps('places')
      .then((maps) => {
        if (cancelled || !inputRef.current) return;
        const Autocomplete = maps.places?.Autocomplete || window.google?.maps?.places?.Autocomplete;
        if (!Autocomplete) throw new Error(pick('La recherche d’adresse Google n’est pas disponible.', 'Google address search is unavailable.'));

        const autocomplete = new Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'geometry', 'address_components', 'place_id'],
          componentRestrictions: { country },
          // Do not restrict to postal addresses: an artisan may use an atelier,
          // an agency, a business name or a city as the professional starting point.
        });
        autocompleteRef.current = autocomplete;

        listener = autocomplete.addListener('place_changed', () => {
          try {
            const place = autocomplete.getPlace();
            const coordinates = place?.geometry?.location
              ? {
                  latitude: place.geometry.location.lat(),
                  longitude: place.geometry.location.lng(),
                }
              : null;
            const parts = parseGoogleAddressComponents(place?.address_components || []);
            const selected = makeGeoLocation({
              ...coordinates,
              formattedAddress: place?.formatted_address || inputRef.current?.value || '',
              placeId: place?.place_id || '',
              ...parts,
            });

            if (!selected) throw new Error(pick('Sélectionnez une adresse dans la liste proposée.', 'Select an address from the suggested list.'));
            selectedAddressRef.current = selected.formattedAddress;
            callbacksRef.current.onAddressChange?.(selected.formattedAddress);
            callbacksRef.current.onLocationChange?.(selected);
            setMessage(pick('Point de départ confirmé. Les distances peuvent maintenant être calculées.', 'Starting point confirmed. Distances can now be calculated.'));
          } catch (error) {
            setMessage(error?.message || pick('Impossible de confirmer cette adresse.', 'This address could not be confirmed.'));
          }
        });

        setMapsReady(true);
        setMapsFailed(false);
      })
      .catch((error) => {
        setMapsReady(false);
        setMapsFailed(true);
        setMessage(error?.message || pick('La recherche d’adresse n’a pas pu être chargée.', 'Address search could not be loaded.'));
      });

    return () => {
      cancelled = true;
      listener?.remove?.();
      autocompleteRef.current = null;
    };
  }, [country, pick]);

  const geocode = async (request) => {
    const maps = await loadGoogleMaps('geocoding');
    const geocoder = new maps.Geocoder();

    const results = await new Promise((resolve, reject) => {
      geocoder.geocode(request, (geocodeResults, status) => {
        if (status === 'OK' && Array.isArray(geocodeResults) && geocodeResults.length) {
          resolve(geocodeResults);
          return;
        }
        if (status === 'ZERO_RESULTS') {
          reject(new Error(pick('Aucune adresse précise n’a été trouvée. Ajoutez le numéro, la rue et la ville.', 'No precise address was found. Add the street number, street and city.')));
          return;
        }
        reject(new Error(pick(`Le géocodage Google a échoué (${status || 'réponse inconnue'}).`, `Google geocoding failed (${status || 'unknown response'}).`)));
      });
    });

    const result = results[0];
    const coordinates = result?.geometry?.location
      ? {
          latitude: result.geometry.location.lat(),
          longitude: result.geometry.location.lng(),
        }
      : null;
    const parts = parseGoogleAddressComponents(result?.address_components || []);
    const selected = makeGeoLocation({
      ...coordinates,
      formattedAddress: result?.formatted_address || value || '',
      placeId: result?.place_id || '',
      ...parts,
    });
    if (!selected) throw new Error(pick('Google Maps n’a pas renvoyé de position exploitable.', 'Google Maps did not return a usable location.'));
    return selected;
  };

  const reverseGeocode = (coordinates) => geocode({
    location: { lat: coordinates.latitude, lng: coordinates.longitude },
  });

  const confirmTypedAddress = async () => {
    const address = String(value || '').trim();
    if (!address) {
      setMessage(pick('Saisissez une adresse avant de la confirmer.', 'Enter an address before confirming it.'));
      return;
    }

    setConfirming(true);
    setMessage('');
    try {
      const selected = await geocode({
        address,
        componentRestrictions: { country },
      });
      selectedAddressRef.current = selected.formattedAddress;
      onAddressChange?.(selected.formattedAddress);
      onLocationChange?.(selected);
      setMessage(pick('Point de départ confirmé. Les distances peuvent maintenant être calculées.', 'Starting point confirmed. Distances can now be calculated.'));
    } catch (error) {
      setMessage(error?.message || pick('Impossible de confirmer cette adresse.', 'This address could not be confirmed.'));
    } finally {
      setConfirming(false);
    }
  };

  const locateMe = async () => {
    setLocating(true);
    setMessage('');
    try {
      const coordinates = await getCurrentPosition();
      let selected;

      if (isGoogleMapsConfigured && !mapsFailed) {
        try {
          selected = await reverseGeocode(coordinates);
        } catch (_) {
          selected = makeGeoLocation({
            ...coordinates,
            formattedAddress: String(value || pick('Position actuelle', 'Current location')).trim(),
          });
        }
      } else {
        selected = makeGeoLocation({
          ...coordinates,
          formattedAddress: String(value || pick('Position actuelle', 'Current location')).trim(),
        });
      }

      if (!selected) throw new Error(pick('Votre position n’a pas pu être enregistrée.', 'Your location could not be saved.'));
      selectedAddressRef.current = selected.formattedAddress || value;
      onAddressChange?.(selected.formattedAddress || value);
      onLocationChange?.(selected);
      setMessage(pick(`Point de départ enregistré${coordinates.accuracy ? ` à ±${Math.round(coordinates.accuracy)} m` : ''}.`, `Starting point saved${coordinates.accuracy ? ` to ±${Math.round(coordinates.accuracy)} m` : ''}.`));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLocating(false);
    }
  };

  const handleMarkerMove = async (coordinates) => {
    try {
      const selected = isGoogleMapsConfigured && !mapsFailed
        ? await reverseGeocode(coordinates)
        : makeGeoLocation({ ...coordinates, formattedAddress: value });
      selectedAddressRef.current = selected.formattedAddress || value;
      onAddressChange?.(selected.formattedAddress || value);
      onLocationChange?.(selected);
      setMessage(pick('Point de départ ajusté sur la carte.', 'Starting point adjusted on the map.'));
    } catch (error) {
      onLocationChange?.(makeGeoLocation({ ...coordinates, formattedAddress: value }));
      setMessage(pick('Position ajustée. L’adresse textuelle reste inchangée.', 'Location adjusted. The written address is unchanged.'));
    }
  };

  const handleManualChange = (event) => {
    const nextValue = event.target.value;
    onAddressChange?.(nextValue);
    if (hasCoordinates || (selectedAddressRef.current && nextValue !== selectedAddressRef.current)) {
      selectedAddressRef.current = '';
      onLocationChange?.(null);
      setMessage(pick('Adresse modifiée : confirmez-la pour réactiver le calcul des distances.', 'Address changed: confirm it to re-enable distance calculations.'));
    }
  };

  const retryMaps = () => {
    resetGoogleMapsLoader();
    window.location.reload();
  };

  return (
    <Wrapper>
      <AutocompleteGlobalStyle />
      <Label htmlFor={id}>{resolvedLabel}{required ? ' *' : ''}</Label>
      <InputShell $confirmed={hasCoordinates} $warning={hasTypedAddress && !hasCoordinates}>
        <SearchOutlinedIcon aria-hidden="true" />
        <AddressInput
          ref={inputRef}
          id={id}
          value={value}
          onChange={handleManualChange}
          placeholder={resolvedPlaceholder}
          required={required}
          autoComplete="street-address"
          inputMode="search"
          enterKeyHint="search"
          aria-describedby={`${id}-status`}
          aria-invalid={hasTypedAddress && !hasCoordinates}
        />
        {hasCoordinates
          ? <CheckCircleOutlineIcon aria-label={pick('Adresse confirmée', 'Address confirmed')} />
          : hasTypedAddress
            ? <ErrorOutlineOutlinedIcon aria-label={pick('Adresse à confirmer', 'Address needs confirmation')} />
            : null}
      </InputShell>

      <Actions>
        {hasTypedAddress && !hasCoordinates && mapsReady && (
          <ConfirmButton type="button" onClick={confirmTypedAddress} disabled={confirming}>
            <LocationOnOutlinedIcon />
            {confirming ? pick('Confirmation…', 'Confirming…') : pick('Confirmer cette adresse', 'Confirm this address')}
          </ConfirmButton>
        )}
        <LocationButton type="button" onClick={locateMe} disabled={locating}>
          <MyLocationOutlinedIcon />
          {locating ? pick('Recherche de votre position…', 'Finding your location…') : pick('Utiliser ma position actuelle', 'Use my current location')}
        </LocationButton>
        <PrivacyText>
          {pick('La position n’est demandée qu’après votre clic et n’est pas suivie en arrière-plan.', 'Your location is requested only after you tap and is not tracked in the background.')}
        </PrivacyText>
      </Actions>

      {!isGoogleMapsConfigured && (
        <SetupMessage role="status">
          {pick('La recherche automatique d’adresse est indisponible sur cette version. Vous pouvez tout de même utiliser votre position actuelle.', 'Automatic address search is unavailable in this version. You can still use your current location.')}
        </SetupMessage>
      )}

      {isGoogleMapsConfigured && !mapsReady && !mapsFailed && (
        <LoadingText>{pick('Chargement de la recherche d’adresse…', 'Loading address search…')}</LoadingText>
      )}

      {isGoogleMapsConfigured && mapsFailed && (
        <SetupMessage role="alert">
          <span>{pick('Google Maps n’est pas autorisé pour cette page. L’administrateur doit vérifier la facturation, les API et le domaine autorisé.', 'Google Maps is not authorized for this page. The administrator must check billing, APIs and the authorized domain.')}</span>
          <RetryButton type="button" onClick={retryMaps}><ReplayOutlinedIcon /> {pick('Recharger après correction', 'Reload after correction')}</RetryButton>
        </SetupMessage>
      )}

      {!hasCoordinates && (
        <UnconfirmedNotice>
          <ErrorOutlineOutlinedIcon />
          <div>
            <strong>{pick('Distance non disponible', 'Distance unavailable')}</strong>
            <span>{pick('Ajoutez puis confirmez votre point de départ pour voir la distance et le temps de trajet vers les chantiers.', 'Add and confirm your starting point to see distance and travel time to jobs.')}</span>
          </div>
        </UnconfirmedNotice>
      )}

      {message && <Feedback id={`${id}-status`} $success={hasCoordinates}>{message}</Feedback>}

      {showMap && hasCoordinates && (
        <MapFrame>
          <GoogleMap
            center={location}
            markers={[{ id: 'selected-location', position: location, label: pick('Adresse sélectionnée', 'Selected address') }]}
            zoom={15}
            draggableMarkerId="selected-location"
            onMarkerDrag={handleMarkerMove}
            ariaLabel={pick('Carte de confirmation de l’adresse', 'Address confirmation map')}
          />
          <MapCaption><LocationOnOutlinedIcon /> {pick('Déplacez le repère pour corriger précisément l’emplacement.', 'Move the pin to fine-tune the location.')}</MapCaption>
        </MapFrame>
      )}
    </Wrapper>
  );
};

const AutocompleteGlobalStyle = createGlobalStyle`
  .pac-container {
    z-index: 2200 !important;
    border: 1px solid #ddd3c4 !important;
    border-radius: .9rem !important;
    margin-top: .25rem !important;
    overflow: hidden !important;
    box-shadow: 0 1rem 2.5rem rgba(0,0,0,.18) !important;
    font-family: inherit !important;
  }
  .pac-item { min-height: 2.9rem; display: flex; align-items: center; padding: .45rem .7rem; cursor: pointer; }
  .pac-item:hover { background: #f7f2e9; }
  .pac-item-query { font-size: .9rem; color: #111; }
`;

const Wrapper = styled.div`
  display: grid;
  gap: 0.65rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;
const Label = styled.label`color: var(--color-mainDark); font-size: 0.82rem; font-weight: 850;`;
const InputShell = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 3.15rem;
  display: grid;
  grid-template-columns: auto minmax(0,1fr) auto;
  align-items: center;
  gap: .55rem;
  box-sizing: border-box;
  padding: 0 .75rem;
  border: 1px solid ${({ $confirmed, $warning }) => ($confirmed ? '#7bb994' : $warning ? '#d8a443' : '#d8d0c5')};
  border-radius: .9rem;
  background: #fff;
  color: ${({ $confirmed, $warning }) => ($confirmed ? '#247047' : $warning ? '#986614' : '#655f57')};
  &:focus-within { outline: 3px solid rgba(192,163,117,.22); border-color: #c0a375; }
  svg { flex: 0 0 auto; font-size: 1.15rem; }
`;
const AddressInput = styled.input`
  width: 100%;
  min-width: 0;
  min-height: 3rem;
  box-sizing: border-box;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #111;
  font: inherit;
  font-size: 16px;
  line-height: 1.35;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .65rem 1rem;
  @media (max-width: 560px) { display: grid; grid-template-columns: 1fr; }
`;
const BaseActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  min-height: 2.75rem;
  max-width: 100%;
  padding: .62rem .82rem;
  border-radius: 999px;
  font: inherit;
  font-size: .8rem;
  font-weight: 850;
  line-height: 1.35;
  white-space: normal;
  cursor: pointer;
  &:disabled { opacity: .55; cursor: wait; }
  svg { flex: 0 0 auto; font-size: 1rem; }
  @media (max-width: 560px) { width: 100%; }
`;
const ConfirmButton = styled(BaseActionButton)`border: 1px solid #111; background: #111; color: #fff;`;
const LocationButton = styled(BaseActionButton)`border: 1px solid #d8d0c5; background: #faf8f4; color: #111;`;
const PrivacyText = styled.span`min-width:0;color:#6f7f8a;font-size:.72rem;line-height:1.45;overflow-wrap:anywhere;`;
const SetupMessage = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: .65rem;
  margin: 0;
  padding: .72rem .8rem;
  border: 1px solid #eed69a;
  border-radius: .75rem;
  background: #fff7df;
  color: #725515;
  font-size: .76rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;
const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  padding: .42rem .62rem;
  border: 1px solid rgba(114,85,21,.25);
  border-radius: 999px;
  background: #fff;
  color: inherit;
  font: inherit;
  font-size: .72rem;
  font-weight: 850;
  cursor: pointer;
  svg { font-size: .9rem; }
`;
const LoadingText = styled.span`min-width:0;color:#71818d;font-size:.78rem;overflow-wrap:anywhere;`;
const UnconfirmedNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: .6rem;
  padding: .72rem .8rem;
  border: 1px solid #ead6ad;
  border-radius: .8rem;
  background: #fffaf0;
  color: #78591c;
  > svg { flex: 0 0 auto; margin-top: .05rem; font-size: 1.05rem; }
  > div { display: grid; gap: .12rem; }
  strong { font-size: .78rem; }
  span { font-size: .73rem; line-height: 1.45; }
`;
const Feedback = styled.p`
  min-width: 0;
  margin: 0;
  color: ${({ $success }) => ($success ? '#22643f' : '#8a4c22')};
  font-size: .78rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;
const MapFrame = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e7dfd2;
  border-radius: 1rem;
  background: #f1ece5;
  > div:first-child { min-height: clamp(13rem, 48vw, 17rem); }
  @media(max-width:560px){ > div:first-child { min-height: 12rem; } }
`;
const MapCaption = styled.div`
  display:flex;
  align-items:flex-start;
  gap:.4rem;
  min-width:0;
  padding:.62rem .75rem;
  color:#5d7080;
  font-size:.72rem;
  line-height:1.45;
  overflow-wrap:anywhere;
  svg{flex:0 0 auto;font-size:1rem;}
`;

export default AddressLocationPicker;
